import z from 'zod'
import { ApiAuthenticationFailedException } from '../errors'
import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'
import { bitbankAccountCategory } from './constants'

const BASE_URL = 'https://api.bitbank.cc'
const TIME_WINDOW = 5000 // 5秒の有効期間

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  dataString: string
  method: 'GET' | 'POST'
  path: string
}): Promise<Record<string, string>> => {
  const { apiKey, apiSecret, createHeaders, dataString, method, path } = params

  // 署名対象文字列の作成
  const requestTime = Date.now()
  let signTarget = ''
  if (method === 'GET') {
    // GETの場合: requestTime + timeWindow + path + queryParams
    signTarget = `${requestTime}${TIME_WINDOW}${path}${dataString}`
  } else if (method === 'POST') {
    // POSTの場合: requestTime + timeWindow + requestBody
    signTarget = `${requestTime}${TIME_WINDOW}${dataString}`
  }

  // HMAC-SHA256署名の生成とhexdigest化
  const signature = await sign({
    algorithm: 'sha256',
    payload: signTarget,
    secret: apiSecret,
    encoding: 'hex',
  })

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/json',
    'ACCESS-KEY': apiKey,
    'ACCESS-SIGNATURE': signature,
    'ACCESS-REQUEST-TIME': requestTime.toString(),
    'ACCESS-TIME-WINDOW': TIME_WINDOW.toString(),
    ...overrideHeaders,
  }
}

export const createClient = ({
  apiKey,
  apiSecret,
  createHeaders,
  proxy,
}: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const queryString = buildQueryString(query)
      const queryWithQuestionMark = queryString ? `?${queryString}` : ''
      const bodyString = body ? JSON.stringify(body) : ''
      const url = `${BASE_URL}${path}${queryWithQuestionMark}`
      const dataString = method === 'GET' ? queryWithQuestionMark : bodyString
      const headers = await createAuthHeaders({
        apiKey,
        apiSecret,
        createHeaders,
        dataString,
        method,
        path,
      })
      return {
        url,
        headers,
        body: method === 'POST' ? bodyString : undefined,
      }
    },
    getDelayForRetry: ({ statusCode }) => {
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        return 1000
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    validateResponse: ({ data }) => {
      const parsed = z
        .object({
          success: z.number(),
          data: z.record(z.unknown()),
        })
        .safeParse(data)
      if (!parsed.success) {
        return {
          status: 'error',
          error: new Error('Invalid response format'),
        }
      }
      if (parsed.data.success === 0) {
        const code =
          'code' in parsed.data.data &&
          typeof parsed.data.data.code === 'number'
            ? parsed.data.data.code
            : undefined
        // 20001: API認証に失敗しました
        // 20002: ACCESS-KEYの値が不正です
        if (code === 20001 || code === 20002) {
          return {
            status: 'error',
            error: new ApiAuthenticationFailedException(bitbankAccountCategory),
          }
        }
        // 20034: ACCESS-REQUEST-TIMEの値が不正です
        // 20035: ACCESS-TIME-WINDOW内にリクエストが送信されませんでした
        if (code === 20034 || code === 20035) {
          return {
            status: 'retry',
            delay: 1000,
            error: new Error(
              'Request time error (invalid ACCESS-REQUEST-TIME or out of ACCESS-TIME-WINDOW)',
            ),
          }
        }
        return {
          status: 'error',
          error: new Error(code?.toString() ?? 'Unknown error'),
        }
      }
      return {
        status: 'success',
      }
    },
    proxy,
  })
